import { useState } from "react";

const HashIcon = () => (
  <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <line x1='4' y1='9' x2='20' y2='9' />
    <line x1='4' y1='15' x2='20' y2='15' />
    <line x1='10' y1='3' x2='8' y2='21' />
    <line x1='16' y1='3' x2='14' y2='21' />
  </svg>
);

const ArrowIcon = () => (
  <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <line x1='7' y1='17' x2='17' y2='7' />
    <polyline points='7 7 17 7 17 17' />
  </svg>
);

const CheckIcon = () => (
  <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <polyline points='20 6 9 17 4 12' />
  </svg>
);

const XIcon = () => (
  <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <line x1='18' y1='6' x2='6' y2='18' />
    <line x1='6' y1='6' x2='18' y2='18' />
  </svg>
);

const PencilIcon = () => (
  <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' />
    <path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' />
  </svg>
);

// ─── SerialNumber Component ───────────────────────────────────────────────────
//
// Props:
//   value     {string|null}  – current serial number (controlled)
//   onChange  {fn}           – called with the saved serial string

export default function SerialNumber({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");

  const handleOpen = () => {
    setInput("");
    setEditing(false);
    setError("");
    setOpen(true);
  };

  const handleEdit = () => {
    setInput(value || "");
    setEditing(true);
    setError("");
    setOpen(true);
  };

  const handleSave = () => {
    if (!input.trim()) {
      setError("Please enter a serial number");
      return;
    }
    if (input.trim().length < 3) {
      setError("Serial number must be at least 3 characters");
      return;
    }
    onChange(input.trim().toUpperCase());
    setOpen(false);
    setInput("");
    setError("");
  };

  const handleCancel = () => {
    setOpen(false);
    setInput("");
    setError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  return (
    <>
      <style>{`
            @keyframes snFadeIn {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
            @keyframes snPopUp {
              from { opacity: 0; transform: translate(-50%, -46%) scale(0.95); }
              to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
            }
          `}</style>

      {/* ── Trigger button / saved badge ── */}
      {!value ? (
        <button
          onClick={handleOpen}
          style={{
            background: open ? "#eff6ff" : "#fafafa",
            color: open ? "#2563eb" : "#64748b",
            border: `1px solid ${open ? "#bfdbfe" : "#e2e8f0"}`,
            borderRadius: "7px",
            padding: "3px 9px",
            fontSize: "11.5px",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            transition: "all 0.15s ease",
            whiteSpace: "nowrap",
          }}
        >
          <HashIcon />
          {open ? "Adding S/N…" : "+ Serial No."}
        </button>
      ) : (
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: "7px",
            padding: "3px 8px 3px 9px",
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <HashIcon />
          <span
            onClick={handleEdit}
            style={{
              fontSize: "11.5px",
              fontWeight: 700,
              color: "#2563eb",
              letterSpacing: "0.03em",
              cursor: "pointer",
              maxWidth: "60px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "wrap",
            }}
          >
            {value}
          </span>
          <button
            onClick={handleEdit}
            title='Edit serial number'
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#93c5fd",
              padding: "1px",
              display: "flex",
              alignItems: "center",
              borderRadius: "3px",
              transition: "color 0.15s",
            }}
          >
            <PencilIcon />
          </button>
        </div>
      )}

      {/* ── Centered overlay form ── */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            onClick={handleCancel}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.5)",
              backdropFilter: "blur(4px)",
              zIndex: 200,
              animation: "snFadeIn 0.18s ease",
            }}
          />

          {/* Modal panel */}
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 201,
              width: "calc(100% - 40px)",
              maxWidth: "340px",
              background: "#fff",
              borderRadius: "18px",
              boxShadow: "0 32px 80px rgba(0,0,0,0.2), 0 4px 20px rgba(0,0,0,0.08)",
              padding: "20px",
              animation: "snPopUp 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
          >
            {/* Header row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div
                  style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "9px",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#2563eb",
                  }}
                >
                  <HashIcon />
                </div>
                <div>
                  <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>
                    {editing ? "Edit Serial Number" : "Add Serial Number"}
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 500 }}>Us material</div>
                </div>
              </div>

              <button
                onClick={handleCancel}
                style={{
                  width: "30px",
                  height: "30px",
                  background: "#f1f5f9",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#64748b",
                  flexShrink: 0,
                  transition: "background 0.15s",
                }}
              >
                <XIcon />
              </button>
            </div>

            {/* Divider */}
            <div style={{ height: "1px", background: "#f1f5f9", marginBottom: "16px" }} />

            {/* Label */}
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: 700,
                color: "#64748b",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                marginBottom: "7px",
              }}
            >
              Serial Number
            </label>

            {/* Input field */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: "#f8fafc",
                border: `1.5px solid ${error ? "#fca5a5" : "#bfdbfe"}`,
                borderRadius: "10px",
                padding: "0 12px",
                marginBottom: "8px",
                transition: "border-color 0.15s",
              }}
            >
              <span style={{ color: "#2563eb", marginRight: "8px", display: "flex", opacity: 0.5 }}>
                <HashIcon />
              </span>
              <input
                autoFocus
                type='text'
                placeholder='e.g. SN-2024-AB1234'
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setError("");
                }}
                onKeyDown={handleKeyDown}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1e293b",
                  width: "100%",
                  padding: "12px 0",
                  letterSpacing: "0.05em",
                  fontFamily: "'DM Mono', 'Courier New', monospace",
                }}
              />
            </div>

            {/* Error / hint */}
            {error ? (
              <p style={{ margin: "0 0 16px", fontSize: "11.5px", color: "#dc2626", fontWeight: 500 }}>{error}</p>
            ) : (
              <p style={{ margin: "0 0 16px", fontSize: "11px", color: "#94a3b8" }}>
                Press{" "}
                <kbd
                  style={{
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: "3px",
                    padding: "1px 5px",
                    fontFamily: "monospace",
                    fontSize: "10px",
                  }}
                >
                  Enter
                </kbd>{" "}
                to save ·{" "}
                <kbd
                  style={{
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: "3px",
                    padding: "1px 5px",
                    fontFamily: "monospace",
                    fontSize: "10px",
                  }}
                >
                  Esc
                </kbd>{" "}
                to dismiss
              </p>
            )}

            {/* Action buttons */}
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1,
                  height: "42px",
                  background: "#fff",
                  color: "#475569",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  flex: 2,
                  height: "42px",
                  background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(59,130,246,0.32)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  transition: "all 0.15s",
                }}
              >
                <CheckIcon />
                {editing ? "Update" : "Save Serial No."}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
